<div class="flex flex-col gap-3">
    <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1 min-w-0">
            <label class="text-sm font-medium" [for]="idPrefix() + '-target'">
                Target average
            </label>
            <p-inputnumber
                [inputId]="idPrefix() + '-target'"
                [ngModel]="targetAverage()"
                (ngModelChange)="targetAverage.set($event ?? 1)"
                [min]="1"
                [max]="5"
                [step]="0.1"
                mode="decimal"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [showButtons]="true"
                buttonLayout="horizontal"
                spinnerMode="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                styleClass="w-full"
            />
        </div>
        <div class="flex flex-col gap-1 min-w-0">
            <label class="text-sm font-medium" [for]="idPrefix() + '-count'">
                Count
            </label>
            <p-inputnumber
                [inputId]="idPrefix() + '-count'"
                [ngModel]="count()"
                (ngModelChange)="count.set($event ?? 1)"
                [min]="1"
                [showButtons]="true"
                buttonLayout="horizontal"
                spinnerMode="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                styleClass="w-full"
            />
        </div>
    </div>

    <div class="flex flex-col gap-1">
        <label
            class="text-sm font-medium"
            [for]="idPrefix() + '-concentration'"
        >
            Concentration
        </label>
        <div class="flex items-center gap-3">
            <span class="text-xs text-muted-color w-12 shrink-0">Even</span>
            <input
                [id]="idPrefix() + '-concentration'"
                type="range"
                class="flex-1 accent-primary-500"
                min="0"
                max="1"
                step="0.01"
                [value]="concentration()"
                (input)="onConcentrationInput($event)"
            />
            <span class="text-xs text-muted-color w-12 shrink-0 text-right">
                Peak
            </span>
        </div>
    </div>

    <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Preview</label>
        <div
            class="border border-surface-200 dark:border-surface-700 rounded p-2 flex flex-col gap-1"
        >
            <div class="flex items-center gap-2 text-xs">
                <span class="w-8 shrink-0 text-muted-color">1★</span>
                <div
                    class="flex-1 bg-surface-100 dark:bg-surface-800 rounded h-3 overflow-hidden"
                >
                    <div
                        class="bg-primary-500 h-full"
                        [style.width.%]="(distribution().Rating1 / maxBucket()) * 100"
                    ></div>
                </div>
                <span class="w-10 shrink-0 text-right">
                    {{ distribution().Rating1 }}
                </span>
            </div>
            <div class="flex items-center gap-2 text-xs">
                <span class="w-8 shrink-0 text-muted-color">2★</span>
                <div
                    class="flex-1 bg-surface-100 dark:bg-surface-800 rounded h-3 overflow-hidden"
                >
                    <div
                        class="bg-primary-500 h-full"
                        [style.width.%]="(distribution().Rating2 / maxBucket()) * 100"
                    ></div>
                </div>
                <span class="w-10 shrink-0 text-right">
                    {{ distribution().Rating2 }}
                </span>
            </div>
            <div class="flex items-center gap-2 text-xs">
                <span class="w-8 shrink-0 text-muted-color">3★</span>
                <div
                    class="flex-1 bg-surface-100 dark:bg-surface-800 rounded h-3 overflow-hidden"
                >
                    <div
                        class="bg-primary-500 h-full"
                        [style.width.%]="(distribution().Rating3 / maxBucket()) * 100"
                    ></div>
                </div>
                <span class="w-10 shrink-0 text-right">
                    {{ distribution().Rating3 }}
                </span>
            </div>
            <div class="flex items-center gap-2 text-xs">
                <span class="w-8 shrink-0 text-muted-color">4★</span>
                <div
                    class="flex-1 bg-surface-100 dark:bg-surface-800 rounded h-3 overflow-hidden"
                >
                    <div
                        class="bg-primary-500 h-full"
                        [style.width.%]="(distribution().Rating4 / maxBucket()) * 100"
                    ></div>
                </div>
                <span class="w-10 shrink-0 text-right">
                    {{ distribution().Rating4 }}
                </span>
            </div>
            <div class="flex items-center gap-2 text-xs">
                <span class="w-8 shrink-0 text-muted-color">5★</span>
                <div
                    class="flex-1 bg-surface-100 dark:bg-surface-800 rounded h-3 overflow-hidden"
                >
                    <div
                        class="bg-primary-500 h-full"
                        [style.width.%]="(distribution().Rating5 / maxBucket()) * 100"
                    ></div>
                </div>
                <span class="w-10 shrink-0 text-right">
                    {{ distribution().Rating5 }}
                </span>
            </div>
        </div>
        <div class="text-xs text-muted-color">
            Achieved average:
            <strong>{{ achievedAverage() | number: '1.2-2' }}</strong>
            (target {{ targetAverage() | number: '1.1-2' }})
        </div>
        @if (validation(); as msg) {
        <p-message severity="warn" [text]="msg" styleClass="m-0" />
        }
    </div>

    @if (tags(); as tagList) {
    <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Tags</label>
        <div
            class="flex flex-wrap gap-2 border border-surface-200 dark:border-surface-700 rounded p-2 min-h-10"
        >
            @for (tag of tagList; track tag) {
            <span
                class="inline-flex items-center rounded bg-surface-100 dark:bg-surface-800 px-2 py-1 text-xs"
            >
                {{ tag }}
            </span>
            }
        </div>
        <span class="text-xs text-muted-color">
            Tags are fixed automatically to General plus the game template's
            tags. Comments are drawn from this pool.
        </span>
    </div>
    }

    <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1 min-w-0">
            <label class="text-sm font-medium" [for]="idPrefix() + '-from'">
                Created from
            </label>
            <p-datepicker
                [inputId]="idPrefix() + '-from'"
                [ngModel]="createdFrom()"
                (ngModelChange)="createdFrom.set($event ?? null)"
                dateFormat="yy-mm-dd"
                [showIcon]="true"
                [showButtonBar]="true"
                appendTo="body"
                styleClass="w-full"
            />
        </div>
        <div class="flex flex-col gap-1 min-w-0">
            <label class="text-sm font-medium" [for]="idPrefix() + '-to'">
                Created to
            </label>
            <p-datepicker
                [inputId]="idPrefix() + '-to'"
                [ngModel]="createdTo()"
                (ngModelChange)="createdTo.set($event ?? null)"
                dateFormat="yy-mm-dd"
                [showIcon]="true"
                [showButtonBar]="true"
                appendTo="body"
                styleClass="w-full"
            />
        </div>
    </div>
</div>
